import { useState, useEffect, useRef } from 'react';
import { PlatformLayout } from '../components/layout/PlatformLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import {
  Shield, ShieldOff, Copy, ExternalLink, Play, Loader2, CheckCircle2, XCircle,
  AlertTriangle, Database, Globe, Server, Activity, Radio, FolderSync, KeyRound,
  Terminal, Search, Wrench, ScrollText, Send, Trash2, RefreshCw, Zap
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatusBadge({ status }) {
  if (status === 'ok' || status === 'reachable' || status === 'active')
    return <Badge data-testid="status-ok" className="bg-emerald-600/15 text-emerald-700 border-emerald-200 text-[11px] gap-1"><CheckCircle2 className="w-3 h-3" /> OK</Badge>;
  if (status === 'error' || status === 'unreachable' || status === 'not_found')
    return <Badge data-testid="status-error" variant="destructive" className="text-[11px] gap-1"><XCircle className="w-3 h-3" /> Erreur</Badge>;
  if (status === 'not_configured')
    return <Badge data-testid="status-warn" className="bg-amber-100 text-amber-700 border-amber-200 text-[11px] gap-1"><AlertTriangle className="w-3 h-3" /> Non config.</Badge>;
  return <Badge variant="secondary" className="text-[11px]">{status}</Badge>;
}

function SectionCard({ icon: Icon, title, children, status }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-muted-foreground" />
            <CardTitle className="text-sm font-medium">{title}</CardTitle>
          </div>
          {status && <StatusBadge status={status} />}
        </div>
      </CardHeader>
      <CardContent className="text-sm">{children}</CardContent>
    </Card>
  );
}

// ── Tab: Diagnostic ─────────────────────────────────────────
function DiagnosticTab({ diagUrl }) {
  const [testing, setTesting] = useState(false);
  const [results, setResults] = useState(null);

  const handleTest = async () => {
    if (!diagUrl) return;
    setTesting(true);
    setResults(null);
    try {
      const res = await axios.get(diagUrl);
      setResults(res.data);
    } catch { toast.error('Erreur lors du test'); }
    finally { setTesting(false); }
  };

  const checks = results?.checks;

  return (
    <div className="space-y-4">
      <Button size="sm" variant="secondary" onClick={handleTest} disabled={testing} data-testid="diag-test-btn">
        {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> : <Play className="w-3.5 h-3.5 mr-2" />}
        Lancer le diagnostic
      </Button>

      {checks && (
        <div className="space-y-4">
          <p className="text-xs text-muted-foreground">{results?.timestamp} — {results?.domain}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SectionCard icon={Database} title="MongoDB" status={checks.mongodb?.status}>
              {checks.mongodb?.status === 'ok'
                ? <p className="text-muted-foreground">{checks.mongodb.collections} collections</p>
                : <p className="text-red-600">{checks.mongodb?.error}</p>}
            </SectionCard>
            <SectionCard icon={Server} title="Donnees" status={checks.data?.status}>
              {checks.data?.status === 'ok' ? (
                <div className="grid grid-cols-2 gap-y-1 gap-x-4 text-muted-foreground">
                  <span>Tenants: <strong className="text-foreground">{checks.data.tenants}</strong></span>
                  <span>Users: <strong className="text-foreground">{checks.data.users}</strong></span>
                  <span>Clones: <strong className="text-foreground">{checks.data.clones}</strong></span>
                  <span>Memories: <strong className="text-foreground">{checks.data.memories}</strong></span>
                  <span>Contacts: <strong className="text-foreground">{checks.data.contacts}</strong></span>
                  <span>Dossiers: <strong className="text-foreground">{checks.data.dossiers}</strong></span>
                </div>
              ) : <p className="text-red-600">{checks.data?.error}</p>}
            </SectionCard>
            <SectionCard icon={Activity} title="Activite 24h" status={checks.activity_24h?.status}>
              {checks.activity_24h?.status === 'ok' ? (
                <div className="space-y-1 text-muted-foreground">
                  <p>Memories: <strong className="text-foreground">{checks.activity_24h.new_memories}</strong></p>
                  <p>Chat: <strong className="text-foreground">{checks.activity_24h.chat_messages}</strong></p>
                  <p>Jobs: <strong className="text-foreground">{checks.activity_24h.background_jobs}</strong></p>
                </div>
              ) : <p className="text-red-600">{checks.activity_24h?.error}</p>}
            </SectionCard>
            <SectionCard icon={FolderSync} title="File de taches" status={checks.task_queue?.status}>
              {checks.task_queue?.status === 'ok' ? (
                <div className="space-y-1 text-muted-foreground">
                  <p>Pending: <strong className="text-foreground">{checks.task_queue.pending}</strong> | Running: <strong className="text-foreground">{checks.task_queue.running}</strong></p>
                  <p>OK (24h): <strong className="text-foreground">{checks.task_queue.completed_24h}</strong> | KO: <strong className={checks.task_queue.failed_24h > 0 ? 'text-red-600' : 'text-foreground'}>{checks.task_queue.failed_24h}</strong></p>
                  <p>Locks: <strong className="text-foreground">{checks.task_queue.active_locks}</strong></p>
                </div>
              ) : <p className="text-red-600">{checks.task_queue?.error}</p>}
            </SectionCard>
            <SectionCard icon={Radio} title="Sources" status={checks.sources?.status}>
              {checks.sources?.status === 'ok' ? (
                <div className="space-y-1 text-muted-foreground">
                  <p>Ringover: <strong className="text-foreground">{checks.sources.ringover?.active}/{checks.sources.ringover?.total}</strong></p>
                  <p>Email: <strong className="text-foreground">{checks.sources.email?.active}/{checks.sources.email?.total}</strong></p>
                  <p>WhatsApp: <strong className="text-foreground">{checks.sources.whatsapp?.active}/{checks.sources.whatsapp?.total}</strong></p>
                </div>
              ) : <p className="text-red-600">{checks.sources?.error}</p>}
            </SectionCard>
            <SectionCard icon={Globe} title="Ringover API" status={checks.ringover_api?.status}>
              {checks.ringover_api?.status === 'ok'
                ? <p className="text-muted-foreground">HTTP {checks.ringover_api.http_code}</p>
                : checks.ringover_api?.status === 'not_configured'
                  ? <p className="text-muted-foreground">{checks.ringover_api.detail}</p>
                  : <p className="text-red-600">{checks.ringover_api?.error || `HTTP ${checks.ringover_api?.http_code}`}</p>}
            </SectionCard>
          </div>

          {checks.endpoints && (
            <SectionCard icon={Globe} title="Endpoints API">
              <div className="space-y-2">
                {checks.endpoints.map((ep, i) => (
                  <div key={i} className="flex items-center justify-between py-1">
                    <span className="text-muted-foreground">{ep.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-muted-foreground">HTTP {ep.http_code}</span>
                      <StatusBadge status={ep.status} />
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}

          {checks.environment && (
            <SectionCard icon={KeyRound} title="Variables d'environnement">
              <div className="space-y-1.5">
                {Object.entries(checks.environment).map(([key, val]) => (
                  <div key={key} className="flex items-center justify-between py-0.5">
                    <code className="text-xs font-mono">{key}</code>
                    <div className="flex items-center gap-2">
                      {val.configured
                        ? <><span className="text-xs text-muted-foreground font-mono">{val.preview}</span><Badge className="bg-emerald-600/15 text-emerald-700 border-emerald-200 text-[10px]">OK</Badge></>
                        : <Badge variant="destructive" className="text-[10px]">Manquant</Badge>}
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}
        </div>
      )}
    </div>
  );
}

// ── Tab: MongoDB Query ──────────────────────────────────────
function QueryTab({ diagUrl }) {
  const [query, setQuery] = useState('{\n  "collection": "persons",\n  "operation": "find",\n  "filter": {},\n  "limit": 10\n}');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleRun = async () => {
    setLoading(true);
    setResult(null);
    try {
      const parsed = JSON.parse(query);
      const res = await axios.post(`${diagUrl}/query`, parsed);
      setResult(res.data);
    } catch (e) {
      setResult({ status: 'error', error: e.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="text-xs font-medium text-muted-foreground">Requete MongoDB (JSON)</label>
        <textarea
          data-testid="query-input"
          className="w-full h-48 font-mono text-xs p-3 border rounded-lg bg-zinc-950 text-emerald-400 resize-y focus:outline-none focus:ring-2 focus:ring-primary"
          value={query}
          onChange={e => setQuery(e.target.value)}
          spellCheck={false}
        />
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={handleRun} disabled={loading} data-testid="query-run-btn">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> : <Search className="w-3.5 h-3.5 mr-2" />}
            Executer
          </Button>
          <span className="text-xs text-muted-foreground">Operations: find, find_one, count, aggregate</span>
        </div>
      </div>
      {result && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium">Resultat</span>
            <StatusBadge status={result.status} />
          </div>
          <pre data-testid="query-result" className="text-xs font-mono p-3 border rounded-lg bg-zinc-950 text-zinc-300 overflow-auto max-h-96 whitespace-pre-wrap">
            {JSON.stringify(result.result || result.error, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

// ── Tab: Mutations ──────────────────────────────────────────
function MutateTab({ diagUrl }) {
  const [query, setQuery] = useState('{\n  "collection": "persons",\n  "operation": "update_one",\n  "filter": {"id": "..."},\n  "update": {"$set": {"name": "..."}}\n}');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleRun = async () => {
    setLoading(true);
    setResult(null);
    try {
      const parsed = JSON.parse(query);
      const res = await axios.post(`${diagUrl}/mutate`, parsed);
      setResult(res.data);
      if (res.data.status === 'ok') toast.success('Mutation executee');
    } catch (e) {
      setResult({ status: 'error', error: e.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-4">
      <div className="p-3 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30">
        <p className="text-xs text-amber-700 dark:text-amber-400 font-medium">Ecriture en base — vos modifications sont irreversibles</p>
      </div>
      <div className="space-y-2">
        <label className="text-xs font-medium text-muted-foreground">Mutation MongoDB (JSON)</label>
        <textarea
          data-testid="mutate-input"
          className="w-full h-48 font-mono text-xs p-3 border rounded-lg bg-zinc-950 text-amber-400 resize-y focus:outline-none focus:ring-2 focus:ring-primary"
          value={query}
          onChange={e => setQuery(e.target.value)}
          spellCheck={false}
        />
        <div className="flex items-center gap-2">
          <Button size="sm" variant="destructive" onClick={handleRun} disabled={loading} data-testid="mutate-run-btn">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> : <Zap className="w-3.5 h-3.5 mr-2" />}
            Executer la mutation
          </Button>
          <span className="text-xs text-muted-foreground">Operations: insert_one, update_one, update_many, delete_one, delete_many</span>
        </div>
      </div>
      {result && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium">Resultat</span>
            <StatusBadge status={result.status} />
          </div>
          <pre data-testid="mutate-result" className="text-xs font-mono p-3 border rounded-lg bg-zinc-950 text-zinc-300 overflow-auto max-h-96 whitespace-pre-wrap">
            {JSON.stringify(result.result || result.error, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

// ── Tab: Actions ────────────────────────────────────────────
function ActionsTab({ diagUrl }) {
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState({});

  const runAction = async (action, params = {}) => {
    setLoading(prev => ({ ...prev, [action]: true }));
    try {
      const res = await axios.post(`${diagUrl}/action`, { action, params });
      setResults(prev => ({ ...prev, [action]: res.data }));
      if (res.data.status === 'ok') toast.success(res.data.result);
    } catch (e) {
      setResults(prev => ({ ...prev, [action]: { status: 'error', error: e.message } }));
    } finally {
      setLoading(prev => ({ ...prev, [action]: false }));
    }
  };

  const actions = [
    { id: 'clear_locks', label: 'Nettoyer les locks', icon: Trash2, desc: 'Supprimer tous les verrous distribues', variant: 'outline' },
    { id: 'clear_stale_running', label: 'Reset taches bloquees', icon: RefreshCw, desc: 'Remettre en pending les taches running >30min', variant: 'outline' },
    { id: 'retry_failed_tasks', label: 'Relancer les echecs', icon: RefreshCw, desc: 'Remettre toutes les taches echouees en pending', variant: 'outline' },
    { id: 'trigger_sync_ringover', label: 'Sync Ringover', icon: Zap, desc: 'Forcer la sync de toutes les sources Ringover', variant: 'default' },
    { id: 'trigger_sync_email', label: 'Sync Email', icon: Zap, desc: 'Forcer la sync de toutes les sources Email', variant: 'default' },
    { id: 'purge_completed_tasks', label: 'Purger les anciennes taches', icon: Trash2, desc: 'Supprimer les taches terminees/echouees >7 jours', variant: 'destructive' },
  ];

  return (
    <div className="space-y-3">
      {actions.map(a => (
        <Card key={a.id}>
          <CardContent className="py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <a.icon className="w-4 h-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">{a.label}</p>
                <p className="text-xs text-muted-foreground">{a.desc}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {results[a.id] && <StatusBadge status={results[a.id].status} />}
              <Button size="sm" variant={a.variant} onClick={() => runAction(a.id)} disabled={loading[a.id]} data-testid={`action-${a.id}`}>
                {loading[a.id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Executer'}
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ── Tab: Terminal ────────────────────────────────────────────
function TerminalTab({ diagUrl }) {
  const [command, setCommand] = useState('');
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const termRef = useRef(null);

  const handleExec = async () => {
    if (!command.trim()) return;
    setLoading(true);
    const cmd = command;
    setCommand('');
    try {
      const res = await axios.post(`${diagUrl}/exec`, { command: cmd, timeout: 30 });
      setHistory(prev => [...prev, { cmd, ...res.data.result, ts: new Date().toLocaleTimeString() }]);
    } catch (e) {
      setHistory(prev => [...prev, { cmd, stderr: e.message, exit_code: -1, ts: new Date().toLocaleTimeString() }]);
    } finally {
      setLoading(false);
      setTimeout(() => termRef.current?.scrollTo(0, termRef.current.scrollHeight), 50);
    }
  };

  return (
    <div className="space-y-3">
      <div className="p-3 rounded-lg border border-red-300 bg-red-50 dark:bg-red-950/30">
        <p className="text-xs text-red-700 dark:text-red-400 font-medium">Execution shell — TEMPORAIRE, phase test uniquement</p>
      </div>

      <div ref={termRef} data-testid="terminal-output" className="h-80 overflow-auto bg-zinc-950 rounded-lg border p-3 font-mono text-xs space-y-3">
        {history.length === 0 && <p className="text-zinc-600">En attente de commandes...</p>}
        {history.map((h, i) => (
          <div key={i}>
            <div className="flex items-center gap-2 text-emerald-500">
              <span className="text-zinc-600">[{h.ts}]</span>
              <span>$ {h.cmd}</span>
              <span className={`ml-auto ${h.exit_code === 0 ? 'text-emerald-500' : 'text-red-500'}`}>exit:{h.exit_code}</span>
            </div>
            {h.stdout && <pre className="text-zinc-300 whitespace-pre-wrap mt-1">{h.stdout}</pre>}
            {h.stderr && <pre className="text-red-400 whitespace-pre-wrap mt-1">{h.stderr}</pre>}
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <div className="flex-1 flex items-center bg-zinc-950 border rounded-lg px-3">
          <span className="text-emerald-500 font-mono text-xs mr-2">$</span>
          <input
            data-testid="terminal-input"
            className="flex-1 bg-transparent text-zinc-200 font-mono text-xs py-2 outline-none"
            value={command}
            onChange={e => setCommand(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleExec()}
            placeholder="Tapez une commande..."
            spellCheck={false}
          />
        </div>
        <Button size="sm" onClick={handleExec} disabled={loading || !command.trim()} data-testid="terminal-send-btn">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
        </Button>
      </div>
    </div>
  );
}

// ── Tab: Audit ──────────────────────────────────────────────
function AuditTab({ diagUrl }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${diagUrl}/audit`);
      setLogs(res.data.logs || []);
    } catch { toast.error('Erreur'); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (diagUrl) fetchLogs(); }, [diagUrl]);

  const typeColors = {
    query: 'bg-blue-100 text-blue-700',
    mutate: 'bg-amber-100 text-amber-700',
    action: 'bg-emerald-100 text-emerald-700',
    exec: 'bg-red-100 text-red-700',
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{logs.length} entrees</span>
        <Button size="sm" variant="outline" onClick={fetchLogs} disabled={loading} data-testid="audit-refresh">
          <RefreshCw className={`w-3.5 h-3.5 mr-2 ${loading ? 'animate-spin' : ''}`} /> Actualiser
        </Button>
      </div>
      <div className="space-y-2 max-h-[500px] overflow-auto">
        {logs.map((log, i) => (
          <Card key={i}>
            <CardContent className="py-2.5 space-y-1">
              <div className="flex items-center gap-2">
                <Badge className={`text-[10px] ${typeColors[log.type] || 'bg-zinc-100 text-zinc-700'}`}>{log.type}</Badge>
                <span className="text-xs text-muted-foreground">{log.timestamp}</span>
                <StatusBadge status={log.result_summary === 'success' ? 'ok' : 'error'} />
              </div>
              <p className="text-xs font-mono text-muted-foreground truncate">{JSON.stringify(log.details)}</p>
              {log.result_preview && (
                <p className="text-xs font-mono text-zinc-500 truncate">{log.result_preview}</p>
              )}
            </CardContent>
          </Card>
        ))}
        {logs.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">Aucune action enregistree</p>}
      </div>
    </div>
  );
}

// ── Main Page ───────────────────────────────────────────────
const TABS = [
  { id: 'diagnostic', label: 'Diagnostic', icon: Activity },
  { id: 'query', label: 'MongoDB', icon: Search },
  { id: 'mutate', label: 'Mutations', icon: Database },
  { id: 'actions', label: 'Actions', icon: Wrench },
  { id: 'terminal', label: 'Terminal', icon: Terminal },
  { id: 'audit', label: 'Audit', icon: ScrollText },
];

export default function PlatformDiagnosticPage() {
  const { token } = useAuth();
  const [diagState, setDiagState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [activeTab, setActiveTab] = useState('diagnostic');

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/diag/admin/status`, { headers: { Authorization: `Bearer ${token}` } });
      setDiagState(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchStatus(); }, []);

  const handleToggle = async () => {
    setToggling(true);
    try {
      const newState = !diagState?.enabled;
      const res = await axios.post(`${API}/diag/admin/toggle`, { enabled: newState }, { headers: { Authorization: `Bearer ${token}` } });
      setDiagState(res.data);
      toast.success(newState ? 'Diagnostic active' : 'Diagnostic desactive');
    } catch { toast.error('Erreur'); }
    finally { setToggling(false); }
  };

  const handleCopy = () => {
    if (diagState?.url) { navigator.clipboard.writeText(diagState.url); toast.success('URL copiee'); }
  };

  if (loading) return <PlatformLayout title="Diagnostic Distant"><div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div></PlatformLayout>;

  const isEnabled = diagState?.enabled;
  const diagUrl = diagState?.url;

  return (
    <PlatformLayout title="Diagnostic Distant">
      <div className="space-y-5 max-w-5xl">
        {/* Control */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`relative w-3 h-3 rounded-full ${isEnabled ? 'bg-emerald-500' : 'bg-zinc-400'}`}>
                  {isEnabled && <div className="absolute inset-0 rounded-full bg-emerald-500 animate-ping opacity-75" />}
                </div>
                <div>
                  <h3 className="font-medium text-sm" data-testid="diag-state">{isEnabled ? 'Diagnostic actif' : 'Diagnostic inactif'}</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {isEnabled ? 'Acces complet : lecture, ecriture, actions, terminal' : 'Activez pour generer un lien d\'intervention temporaire'}
                  </p>
                </div>
              </div>
              <Button data-testid="diag-toggle-btn" variant={isEnabled ? 'destructive' : 'default'} size="sm" onClick={handleToggle} disabled={toggling}>
                {toggling ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : isEnabled ? <ShieldOff className="w-4 h-4 mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
                {isEnabled ? 'Desactiver' : 'Activer'}
              </Button>
            </div>

            {isEnabled && diagUrl && (
              <div className="mt-4 p-3 bg-muted/50 rounded-lg border">
                <p className="text-xs text-muted-foreground mb-2 font-medium">URL d'intervention (partageable)</p>
                <div className="flex items-center gap-2">
                  <code data-testid="diag-url" className="flex-1 text-xs bg-background px-3 py-2 rounded border font-mono truncate">{diagUrl}</code>
                  <Button variant="outline" size="icon" className="h-8 w-8 shrink-0" onClick={handleCopy} data-testid="diag-copy-btn"><Copy className="w-3.5 h-3.5" /></Button>
                  <Button variant="outline" size="icon" className="h-8 w-8 shrink-0" asChild>
                    <a href={diagUrl} target="_blank" rel="noopener noreferrer" data-testid="diag-open-btn"><ExternalLink className="w-3.5 h-3.5" /></a>
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Warning when active */}
        {isEnabled && (
          <div className="p-3 rounded-lg border border-red-300 bg-red-50 dark:bg-red-950/30 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />
            <p className="text-xs text-red-700 dark:text-red-400">Mode intervention actif — Acces complet au systeme via le lien. Desactivez apres utilisation.</p>
          </div>
        )}

        {/* Tabs */}
        {isEnabled && diagUrl && (
          <>
            <div className="flex gap-1 border-b" data-testid="diag-tabs">
              {TABS.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    data-testid={`tab-${tab.id}`}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-primary text-primary'
                        : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" /> {tab.label}
                  </button>
                );
              })}
            </div>

            <div>
              {activeTab === 'diagnostic' && <DiagnosticTab diagUrl={diagUrl} />}
              {activeTab === 'query' && <QueryTab diagUrl={diagUrl} />}
              {activeTab === 'mutate' && <MutateTab diagUrl={diagUrl} />}
              {activeTab === 'actions' && <ActionsTab diagUrl={diagUrl} />}
              {activeTab === 'terminal' && <TerminalTab diagUrl={diagUrl} />}
              {activeTab === 'audit' && <AuditTab diagUrl={diagUrl} />}
            </div>
          </>
        )}
      </div>
    </PlatformLayout>
  );
}
